import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  User, Briefcase, Code, Award, Trophy, ExternalLink, Github, Linkedin,
  Globe, MapPin, Calendar, CheckCircle, Crown, Building2, GraduationCap,
  Loader, Share2, FileText, Activity, Clock, Terminal, Download, ShieldCheck,
  ArrowRight, Sparkles, Star
} from 'lucide-react';
import axios from 'axios';
import VerifiedBadge from '../components/VerifiedBadge';
import { getFrontendUrl } from '../utils/urlConfig';

const API = process.env.REACT_APP_BACKEND_URL;

// Premium Swiss design system — White + Deep Ruby + Verified Gold
const c = {
  primary: '#B91C1C',
  primaryDark: '#7F1D1D',
  primaryLight: '#FEF2F2',
  gold: '#B45309',
  goldSoft: '#FFFBEB',
  text: '#0A0A0A',
  textSecondary: '#525252',
  textTertiary: '#A3A3A3',
  background: '#FAFAF9',
  surface: '#FFFFFF',
  subtle: '#F5F5F5',
  border: '#E5E5E5',
  success: '#059669'
};

const tierAccents = {
  Legendary: { fg: '#92400E', bg: '#FFFBEB' },
  Elite: { fg: '#5B21B6', bg: '#F5F3FF' },
  Expert: { fg: '#0E7490', bg: '#ECFEFF' },
  Advanced: { fg: '#047857', bg: '#ECFDF5' },
  Intermediate: { fg: '#B45309', bg: '#FFFBEB' },
  Beginner: { fg: '#525252', bg: '#F5F5F5' }
};

const PublicProfilePage = () => {
  const { username } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/profile/${username}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Profile not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: c.background }}>
        <Loader className="animate-spin" style={{ color: c.primary }} size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: c.background }}>
        <div className="text-center px-6">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border" style={{ borderColor: c.border, borderRadius: '2px', background: c.surface }}>
            <User size={28} style={{ color: c.textTertiary }} />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: c.text }}>Profile Not Found</h1>
          <p className="mt-2 text-sm" style={{ color: c.textSecondary }}>{error}</p>
          <Link to="/codearena" className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 text-xs font-bold tracking-wide uppercase text-white" style={{ background: c.text, borderRadius: '2px' }}>
            Go to EvalentX <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    );
  }

  const { student, resume, verified_data, verified_talent, parsed_resume, stats, recent_activity } = data;
  const talentScore = verified_data?.talent_score;
  const ranking = verified_data?.ranking;
  const isVerified = !!verified_talent?.is_verified;
  const tierName = talentScore?.tier?.name || 'Beginner';
  const tier = tierAccents[tierName] || tierAccents.Beginner;

  const onShare = async () => {
    // Use the OG share endpoint so LinkedIn/WhatsApp/Twitter render a rich
    // preview card with the verified badge, score, and tier.
    const url = `${getFrontendUrl()}/api/share/profile/${encodeURIComponent(username)}`;
    const text = `${student?.full_name || 'EvalentX Verified Talent'} — view this verified profile on EvalentX`;
    if (navigator.share) {
      try { await navigator.share({ title: 'EvalentX Profile', text, url }); } catch { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(url); alert('Profile link copied to clipboard'); } catch { /* ignore */ }
    }
  };

  const downloadVerifiedCertificate = async () => {
    const token = localStorage.getItem('codearena_token');
    if (!token) {
      alert('Please log in as this student to download the certificate.');
      return;
    }
    try {
      const res = await axios.get(`${API}/api/verified-talent/certificate`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `EvalentX_Verified_Talent_${(student?.full_name || 'candidate').replace(/[^A-Za-z0-9]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not download certificate (only available to the verified candidate while logged in).');
    }
  };

  const initial = (student?.full_name?.charAt(0) || 'U').toUpperCase();

  return (
    <div className="min-h-screen" style={{ background: c.background, color: c.text, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 border-b" style={{ background: c.surface, borderColor: c.border }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Link to="/codearena" className="flex items-center gap-2 shrink-0 group">
            <div className="w-7 h-7 flex items-center justify-center" style={{ background: c.primary, borderRadius: '2px' }}>
              <Sparkles size={14} className="text-white" />
            </div>
            <span className="font-extrabold tracking-tight text-sm sm:text-base" style={{ fontFamily: "'Space Grotesk', sans-serif", color: c.text }}>EvalentX</span>
            <span className="hidden sm:inline-block px-1.5 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase border" style={{ borderColor: c.border, color: c.textTertiary, borderRadius: '1px' }}>Public Profile</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onShare}
              className="px-3 py-2 border text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition-colors hover:bg-[#FAFAF9]"
              style={{ borderColor: c.border, color: c.text, borderRadius: '2px' }}
              data-testid="share-profile-btn"
            >
              <Share2 size={14} />
              <span className="hidden xs:inline">Share</span>
            </button>
            <Link
              to={`/resume/${username}`}
              className="px-3 py-2 border text-xs sm:text-sm font-semibold flex items-center gap-1.5 transition-colors hover:bg-[#FAFAF9]"
              style={{ borderColor: c.border, color: c.text, borderRadius: '2px' }}
            >
              <FileText size={14} />
              <span className="hidden sm:inline">Resume</span>
            </Link>
            {isVerified && (
              <button
                onClick={downloadVerifiedCertificate}
                className="px-3 py-2 text-xs sm:text-sm font-bold tracking-wide uppercase flex items-center gap-1.5 transition-transform hover:-translate-y-0.5"
                style={{ background: c.text, color: '#fff', borderRadius: '2px' }}
                data-testid="download-verified-pdf-btn"
              >
                <Download size={14} />
                <span className="hidden sm:inline">Verified PDF</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Verified ribbon */}
        {isVerified && (
          <div
            className="mb-5 px-4 py-3 border flex items-center gap-3 flex-wrap"
            style={{ background: c.goldSoft, borderColor: '#FCD34D', borderRadius: '2px' }}
            data-testid="verified-ribbon"
          >
            <div className="w-7 h-7 flex items-center justify-center" style={{ background: c.gold, borderRadius: '2px' }}>
              <ShieldCheck size={14} className="text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: c.gold }}>EvalentX Verified Talent</p>
              <p className="text-sm font-semibold" style={{ color: c.text }}>
                AI-interview verified · Score {Math.round(verified_talent.best_score || 0)}%
                {verified_talent.plan_name && <span className="text-[#A16207]"> · {verified_talent.plan_name}</span>}
                {verified_talent.badge_expires_at && (
                  <span className="font-normal" style={{ color: c.textTertiary }}> · valid until {new Date(verified_talent.badge_expires_at).toLocaleDateString()}</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Hero Card */}
        <section
          className="bg-white border p-5 sm:p-8"
          style={{ borderColor: c.border, borderRadius: '2px' }}
          data-testid="profile-hero-card"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] gap-6 lg:gap-8 items-start">
            {/* Avatar */}
            <div className="flex items-start gap-4 sm:gap-5">
              <div
                className="w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center font-extrabold text-3xl sm:text-4xl shrink-0 relative"
                style={{
                  background: isVerified
                    ? 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)'
                    : c.subtle,
                  color: isVerified ? c.gold : c.text,
                  borderRadius: '2px',
                  fontFamily: "'Space Grotesk', sans-serif",
                  boxShadow: isVerified ? '0 0 0 2px #FCD34D' : `0 0 0 1px ${c.border}`
                }}
              >
                {initial}
                {isVerified && (
                  <div className="absolute -bottom-1.5 -right-1.5 w-7 h-7 flex items-center justify-center" style={{ background: c.gold, borderRadius: '999px', boxShadow: '0 0 0 2px #fff' }}>
                    <ShieldCheck size={14} className="text-white" />
                  </div>
                )}
              </div>

              <div className="lg:hidden flex-1 min-w-0">
                <h1
                  className="text-2xl font-extrabold tracking-tight break-words"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", color: c.text }}
                  data-testid="profile-candidate-name"
                >
                  {student?.full_name}
                </h1>
                <p className="text-sm mt-1" style={{ color: c.textSecondary }}>{resume?.headline || 'Developer'}</p>
                {isVerified && (
                  <span data-testid="profile-verified-badge-mobile" className="inline-block mt-2">
                    <VerifiedBadge isVerified planCode={verified_talent.plan_code} size="md" />
                  </span>
                )}
              </div>
            </div>

            {/* Name + meta */}
            <div className="hidden lg:block min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1
                  className="text-3xl xl:text-4xl font-extrabold tracking-tighter break-words"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", color: c.text }}
                  data-testid="profile-candidate-name-desktop"
                >
                  {student?.full_name}
                </h1>
                {isVerified && (
                  <span data-testid="profile-verified-badge-dark">
                    <VerifiedBadge isVerified planCode={verified_talent.plan_code} size="lg" />
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-base" style={{ color: c.textSecondary }}>{resume?.headline || 'Developer'}</p>

              {/* Meta chips */}
              <div className="mt-4 flex items-center gap-x-5 gap-y-2 flex-wrap text-xs" style={{ color: c.textSecondary }}>
                {student?.college && (
                  <span className="flex items-center gap-1.5">
                    <GraduationCap size={14} style={{ color: c.textTertiary }} />
                    <span className="font-semibold" style={{ color: c.text }}>{student.college}</span>
                  </span>
                )}
                {student?.location && (
                  <span className="flex items-center gap-1.5">
                    <MapPin size={14} style={{ color: c.textTertiary }} />
                    {student.location}
                  </span>
                )}
                {student?.created_at && (
                  <span className="flex items-center gap-1.5">
                    <Calendar size={14} style={{ color: c.textTertiary }} />
                    Joined {new Date(student.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                )}
              </div>

              {/* Social links */}
              {(student?.github_url || student?.linkedin_url || student?.portfolio_url) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {student?.github_url && (
                    <a href={student.github_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 border text-xs font-semibold transition-colors hover:bg-[#FAFAF9]"
                      style={{ borderColor: c.border, color: c.text, borderRadius: '2px' }}>
                      <Github size={13} /> GitHub
                    </a>
                  )}
                  {student?.linkedin_url && (
                    <a href={student.linkedin_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white transition-transform hover:-translate-y-0.5"
                      style={{ background: '#0A66C2', borderRadius: '2px' }}>
                      <Linkedin size={13} /> LinkedIn
                    </a>
                  )}
                  {student?.portfolio_url && (
                    <a href={student.portfolio_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 border text-xs font-semibold transition-colors hover:bg-[#FAFAF9]"
                      style={{ borderColor: c.border, color: c.text, borderRadius: '2px' }}>
                      <Globe size={13} /> Portfolio
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* CVTS Score Block */}
            <div
              className="border p-5 hidden lg:block min-w-[180px]"
              style={{
                borderColor: isVerified ? '#FCD34D' : c.border,
                background: isVerified ? c.goldSoft : c.subtle,
                borderRadius: '2px'
              }}
              data-testid="profile-cvts-card"
            >
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: isVerified ? c.gold : c.textTertiary }}>
                Talent Score
              </p>
              <p className="text-5xl font-extrabold mt-1 tabular-nums tracking-tighter"
                style={{ fontFamily: "'IBM Plex Mono', monospace", color: isVerified ? c.gold : c.text }}>
                {talentScore?.score || 0}
              </p>
              <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase"
                style={{ background: tier.bg, color: tier.fg, borderRadius: '1px' }}>
                <Crown size={11} />
                {tierName}
              </div>
              {ranking?.rank && (
                <p className="mt-3 text-[11px] font-semibold" style={{ color: c.textSecondary }}>
                  Rank <span className="tabular-nums" style={{ color: c.text, fontFamily: "'IBM Plex Mono', monospace" }}>#{ranking.rank}</span>
                  <span style={{ color: c.textTertiary }}> / {ranking.total_developers || 0}</span>
                </p>
              )}
            </div>
          </div>

          {/* Mobile meta + social */}
          <div className="lg:hidden mt-5 pt-5 border-t" style={{ borderColor: c.border }}>
            <div className="flex items-center gap-x-4 gap-y-2 flex-wrap text-xs" style={{ color: c.textSecondary }}>
              {student?.college && (
                <span className="flex items-center gap-1.5">
                  <GraduationCap size={13} style={{ color: c.textTertiary }} />
                  <span className="font-semibold" style={{ color: c.text }}>{student.college}</span>
                </span>
              )}
              {student?.location && (
                <span className="flex items-center gap-1.5">
                  <MapPin size={13} style={{ color: c.textTertiary }} /> {student.location}
                </span>
              )}
              {student?.created_at && (
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} style={{ color: c.textTertiary }} />
                  Joined {new Date(student.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
            {(student?.github_url || student?.linkedin_url || student?.portfolio_url) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {student?.github_url && (
                  <a href={student.github_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 border text-xs font-semibold"
                    style={{ borderColor: c.border, color: c.text, borderRadius: '2px' }}>
                    <Github size={13} /> GitHub
                  </a>
                )}
                {student?.linkedin_url && (
                  <a href={student.linkedin_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white"
                    style={{ background: '#0A66C2', borderRadius: '2px' }}>
                    <Linkedin size={13} /> LinkedIn
                  </a>
                )}
                {student?.portfolio_url && (
                  <a href={student.portfolio_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 border text-xs font-semibold"
                    style={{ borderColor: c.border, color: c.text, borderRadius: '2px' }}>
                    <Globe size={13} /> Portfolio
                  </a>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Mobile CVTS row */}
        <div
          className="lg:hidden grid grid-cols-2 gap-3 mt-4"
          data-testid="profile-cvts-card-mobile"
        >
          <div className="border p-4"
            style={{
              borderColor: isVerified ? '#FCD34D' : c.border,
              background: isVerified ? c.goldSoft : c.surface,
              borderRadius: '2px'
            }}>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: isVerified ? c.gold : c.textTertiary }}>CVTS</p>
            <p className="text-3xl font-extrabold tabular-nums tracking-tighter mt-1"
              style={{ fontFamily: "'IBM Plex Mono', monospace", color: isVerified ? c.gold : c.text }}>
              {talentScore?.score || 0}
            </p>
          </div>
          <div className="border p-4" style={{ borderColor: c.border, background: c.surface, borderRadius: '2px' }}>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: c.textTertiary }}>Tier</p>
            <p className="text-base font-bold mt-1 flex items-center gap-1.5" style={{ color: c.text, fontFamily: "'Space Grotesk', sans-serif" }}>
              <Crown size={15} style={{ color: c.gold }} />
              {tierName}
            </p>
          </div>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5" data-testid="profile-stat-strip">
          {[
            { label: 'National Rank', value: ranking?.rank ? `#${ranking.rank}` : '—', sub: ranking?.total_developers ? `of ${ranking.total_developers}` : 'unranked', icon: Crown, testid: 'profile-rank-card' },
            { label: 'Problems Solved', value: stats?.accepted_submissions || 0, sub: `${stats?.total_submissions || 0} attempts`, icon: Code },
            { label: 'Projects', value: stats?.projects_count || 0, sub: 'verified', icon: Briefcase },
            { label: 'Achievements', value: stats?.achievements_count || 0, sub: 'badges earned', icon: Award }
          ].map((s, i) => (
            <div key={i}
              className="bg-white border p-4 transition-all"
              style={{ borderColor: c.border, borderRadius: '2px' }}
              data-testid={s.testid || `profile-stat-${i}`}>
              <div className="flex items-start justify-between">
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: c.textTertiary }}>
                  {s.label}
                </p>
                <s.icon size={13} style={{ color: c.textTertiary }} />
              </div>
              <p className="text-2xl font-extrabold mt-2 tabular-nums tracking-tighter"
                style={{ fontFamily: "'Space Grotesk', sans-serif", color: c.text }}>
                {s.value}
              </p>
              <p className="text-[11px] mt-0.5" style={{ color: c.textTertiary }}>{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-7 border-b flex gap-1" style={{ borderColor: c.border }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'projects', label: 'Projects' },
            { id: 'activity', label: 'Activity' }
          ].map(t => {
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`profile-tab-${t.id}`}
                className="px-4 py-3 text-xs font-bold tracking-[0.15em] uppercase transition-colors"
                style={{
                  color: active ? c.text : c.textTertiary,
                  borderBottom: active ? `2px solid ${c.primary}` : '2px solid transparent',
                  marginBottom: '-1px'
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
          <div className="lg:col-span-2 space-y-5">
            {activeTab === 'overview' && (
              <>
                {/* About */}
                {(resume?.summary || student?.bio) && (
                  <Section title="About" icon={User}>
                    <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: c.textSecondary }}>
                      {resume?.summary || student?.bio}
                    </p>
                  </Section>
                )}

                {/* Skills */}
                {student?.skills?.length > 0 && (
                  <Section
                    title="Skills"
                    icon={Code}
                    rightSlot={isVerified && (
                      <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase" style={{ background: '#ECFDF5', color: c.success, borderRadius: '1px' }}>
                        Verified
                      </span>
                    )}
                  >
                    <div className="flex flex-wrap gap-2">
                      {student.skills.map((skill, idx) => (
                        <span key={idx}
                          className="px-2.5 py-1 text-xs font-semibold border"
                          style={{
                            background: c.surface,
                            borderColor: c.border,
                            color: c.text,
                            borderRadius: '2px'
                          }}>
                          {skill}
                        </span>
                      ))}
                    </div>
                    {student?.domains?.length > 0 && (
                      <div className="mt-4 pt-4 border-t" style={{ borderColor: c.border }}>
                        <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: c.textTertiary }}>Focus Areas</p>
                        <div className="flex flex-wrap gap-2">
                          {student.domains.map((d, idx) => (
                            <span key={idx} className="px-2 py-0.5 text-[11px] font-semibold"
                              style={{ background: c.primaryLight, color: c.primary, borderRadius: '1px' }}>
                              {d}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </Section>
                )}

                {/* Experience */}
                {Array.isArray(verified_data?.experience) && verified_data.experience.length > 0 && (
                  <Section title="Experience" icon={Briefcase}>
                    <div className="space-y-4">
                      {verified_data.experience.map((exp, idx) => (
                        <div key={idx} className="pl-4 border-l-2" style={{ borderColor: c.primary }}>
                          <p className="font-bold text-sm" style={{ color: c.text }}>{exp.title || exp.role}</p>
                          <p className="text-xs mt-0.5" style={{ color: c.textSecondary }}>
                            {exp.company}{exp.location && ` · ${exp.location}`}
                          </p>
                          {(exp.start_date || exp.end_date) && (
                            <p className="text-[11px] mt-0.5" style={{ color: c.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>
                              {exp.start_date} — {exp.end_date || 'Present'}
                            </p>
                          )}
                          {exp.description && (
                            <p className="text-xs mt-2 leading-relaxed" style={{ color: c.textSecondary }}>{exp.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Education */}
                {(student?.college || student?.university) && (
                  <Section title="Education" icon={GraduationCap}>
                    <div className="pl-4 border-l-2" style={{ borderColor: c.text }}>
                      <p className="font-bold text-sm" style={{ color: c.text }}>
                        {student?.department || 'Undergraduate Studies'}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: c.textSecondary }}>
                        {student.college || student.university}
                      </p>
                      {student.year_of_study && (
                        <p className="text-[11px] mt-0.5" style={{ color: c.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>
                          Year {student.year_of_study}
                        </p>
                      )}
                    </div>
                  </Section>
                )}

                {/* Resume */}
                {parsed_resume?.resume_text && (
                  <Section
                    title="Resume"
                    icon={FileText}
                    testid="profile-parsed-resume-section"
                    rightSlot={(
                      <div className="flex items-center gap-2">
                        {isVerified && (
                          <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase flex items-center gap-1"
                            style={{ background: '#ECFDF5', color: c.success, borderRadius: '1px' }}>
                            <CheckCircle size={11} /> AI-verified
                          </span>
                        )}
                        {parsed_resume.pdf_url && (
                          <a href={parsed_resume.pdf_url} target="_blank" rel="noopener noreferrer"
                            className="text-[11px] font-bold tracking-wide uppercase inline-flex items-center gap-1 hover:underline"
                            style={{ color: c.primary }}>
                            <Download size={11} /> PDF
                          </a>
                        )}
                      </div>
                    )}
                  >
                    <div className="border p-4 max-h-96 overflow-y-auto" style={{ borderColor: c.border, background: c.subtle, borderRadius: '2px' }}>
                      <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed font-sans" style={{ color: c.text }}>
                        {parsed_resume.resume_text}
                      </pre>
                    </div>
                    <p className="text-[11px] mt-2" style={{ color: c.textTertiary }}>
                      Extracted from {parsed_resume.filename || 'resume.pdf'} · {parsed_resume.text_length?.toLocaleString()} characters
                    </p>
                  </Section>
                )}

                {/* Hiring Challenges */}
                {verified_data?.hiring_challenges?.length > 0 && (
                  <Section
                    title="Hiring Challenges"
                    icon={Building2}
                    rightSlot={(
                      <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase" style={{ background: '#ECFDF5', color: c.success, borderRadius: '1px' }}>
                        Verified
                      </span>
                    )}
                  >
                    <div className="space-y-2">
                      {verified_data.hiring_challenges.map((challenge, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border" style={{ borderColor: c.border, borderRadius: '2px' }}>
                          <div className="min-w-0">
                            <p className="font-bold text-sm truncate" style={{ color: c.text }}>{challenge.title}</p>
                            <p className="text-[11px]" style={{ color: c.textTertiary }}>{challenge.company}</p>
                          </div>
                          <div className="text-right shrink-0 ml-3">
                            <p className="text-sm font-extrabold tabular-nums" style={{ color: c.primary, fontFamily: "'IBM Plex Mono', monospace" }}>
                              {challenge.score} pts
                            </p>
                            {challenge.is_shortlisted && (
                              <p className="text-[10px] font-bold tracking-wide uppercase" style={{ color: c.success }}>Shortlisted</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}
              </>
            )}

            {activeTab === 'projects' && (
              <Section
                title="Projects"
                icon={Briefcase}
                rightSlot={(
                  <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase" style={{ background: '#ECFDF5', color: c.success, borderRadius: '1px' }}>
                    Verified
                  </span>
                )}
              >
                {verified_data?.projects?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {verified_data.projects.map((project, idx) => (
                      <div key={idx} className="border p-4 flex flex-col" style={{ borderColor: c.border, borderRadius: '2px' }}>
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-bold text-sm" style={{ color: c.text, fontFamily: "'Space Grotesk', sans-serif" }}>
                            {project.title}
                          </p>
                          <div className="flex gap-1 shrink-0">
                            {project.github_url && (
                              <a href={project.github_url} target="_blank" rel="noopener noreferrer"
                                className="p-1.5 border transition-colors hover:bg-[#FAFAF9]"
                                style={{ borderColor: c.border, borderRadius: '2px' }}>
                                <Github size={13} style={{ color: c.text }} />
                              </a>
                            )}
                            {project.live_url && (
                              <a href={project.live_url} target="_blank" rel="noopener noreferrer"
                                className="p-1.5 border transition-colors hover:bg-[#FAFAF9]"
                                style={{ borderColor: c.border, borderRadius: '2px' }}>
                                <ExternalLink size={13} style={{ color: c.text }} />
                              </a>
                            )}
                          </div>
                        </div>
                        <p className="text-xs mt-2 leading-relaxed flex-1" style={{ color: c.textSecondary }}>
                          {project.description}
                        </p>
                        {project.tech_stack?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t" style={{ borderColor: c.border }}>
                            {project.tech_stack.map((tech, i) => (
                              <span key={i} className="px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase"
                                style={{ background: c.subtle, color: c.text, borderRadius: '1px' }}>
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-sm" style={{ color: c.textTertiary }}>No projects yet</p>
                )}
              </Section>
            )}

            {activeTab === 'activity' && (
              <Section title="Recent Activity" icon={Activity}>
                {recent_activity?.length > 0 ? (
                  <div className="space-y-1.5">
                    {recent_activity.map((activity, idx) => {
                      const accepted = activity.verdict === 'Accepted';
                      return (
                        <div key={idx} className="flex items-center gap-3 p-3 border" style={{ borderColor: c.border, borderRadius: '2px' }}>
                          <div className="w-8 h-8 flex items-center justify-center shrink-0"
                            style={{
                              background: accepted ? '#ECFDF5' : c.primaryLight,
                              borderRadius: '2px'
                            }}>
                            {accepted ? <CheckCircle size={15} style={{ color: c.success }} /> : <Terminal size={15} style={{ color: c.primary }} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold truncate" style={{ color: c.text }}>
                              <span style={{ color: accepted ? c.success : c.primary }}>{activity.verdict}</span>
                              {' · '}{activity.problem_id}
                            </p>
                            <p className="text-[11px]" style={{ color: c.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>
                              {activity.language}
                            </p>
                          </div>
                          <span className="text-[11px] shrink-0" style={{ color: c.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {new Date(activity.submitted_at).toLocaleDateString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center py-8 text-sm" style={{ color: c.textTertiary }}>No recent activity</p>
                )}
              </Section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-5">
            {/* CVTS Breakdown */}
            <Section title="Talent Score Breakdown" icon={Star}>
              <div className="space-y-3">
                {Object.entries(talentScore?.breakdown || {})
                  .filter(([key]) => key !== 'total' && key !== 'percentile')
                  .map(([key, value]) => {
                    const score = value?.score || 0;
                    const max = value?.max || 100;
                    const pct = (score / max) * 100;
                    return (
                      <div key={key}>
                        <div className="flex justify-between text-[11px] mb-1">
                          <span className="font-bold tracking-wide uppercase" style={{ color: c.textSecondary }}>{key}</span>
                          <span className="tabular-nums font-bold" style={{ color: c.text, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {score}<span style={{ color: c.textTertiary }}>/{max}</span>
                          </span>
                        </div>
                        <div className="h-1 w-full" style={{ background: c.subtle }}>
                          <div className="h-full transition-all" style={{ width: `${pct}%`, background: c.primary }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </Section>

            {/* Achievements */}
            {verified_data?.achievements?.length > 0 && (
              <Section title="Achievements" icon={Trophy}>
                <div className="space-y-1.5">
                  {verified_data.achievements.slice(0, 6).map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-2 border"
                      style={{ borderColor: c.border, background: c.surface, borderRadius: '2px' }}>
                      <Award size={14} style={{ color: c.gold }} />
                      <span className="text-xs font-semibold" style={{ color: c.text }}>{a.title}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* CTA: hire / connect */}
            <div className="border p-5" style={{ borderColor: c.text, background: c.text, borderRadius: '2px' }}>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/70">Recruiter</p>
              <p className="text-base font-extrabold mt-1 text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Hire {student?.full_name?.split(' ')[0] || 'this talent'}
              </p>
              <p className="text-xs mt-1 text-white/70">
                Reach out via verified channels — LinkedIn, email, or portfolio.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {student?.linkedin_url && (
                  <a href={student.linkedin_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold tracking-wide uppercase text-white"
                    style={{ background: '#0A66C2', borderRadius: '2px' }}>
                    <Linkedin size={12} /> LinkedIn
                  </a>
                )}
                <button
                  type="button"
                  onClick={onShare}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold tracking-wide uppercase border border-white/30 text-white hover:bg-white/10 transition-colors"
                  style={{ borderRadius: '2px' }}
                >
                  <Share2 size={12} /> Share
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t mt-12" style={{ background: c.surface, borderColor: c.border }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs" style={{ color: c.textSecondary }}>
            <div className="w-5 h-5 flex items-center justify-center" style={{ background: c.primary, borderRadius: '2px' }}>
              <Sparkles size={10} className="text-white" />
            </div>
            Powered by <strong style={{ color: c.text, fontFamily: "'Space Grotesk', sans-serif" }}>EvalentX</strong>
          </div>
          <div className="flex items-center gap-1.5 text-[11px]" style={{ color: c.textTertiary }}>
            <CheckCircle size={11} style={{ color: c.success }} />
            All data verified from platform activity
          </div>
        </div>
      </footer>
    </div>
  );
};

// Reusable section card
const Section = ({ title, icon: Icon, children, rightSlot, testid }) => (
  <div className="bg-white border p-5" style={{ borderColor: c.border, borderRadius: '2px' }} data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={15} style={{ color: c.primary }} />}
        <h2 className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: c.text }}>
          {title}
        </h2>
      </div>
      {rightSlot}
    </div>
    {children}
  </div>
);

export default PublicProfilePage;
